import { useState } from "react";
// Redux
import { useSelector } from "react-redux";
// React Query
import { useMutation, useQueries, useQueryClient } from "@tanstack/react-query";
// MUI
import { Grid, Skeleton, Typography } from "@mui/material";
// Third Party
import _ from "lodash";

// Components
import BodyLayer from "@/Layouts/components/Layers/BodyLayer";
import RoundedBox from "@/Layouts/components/Basics/RoundedBox";
import EmptyBody from "@/Layouts/components/Basics/EmptyBody";
import GroupSelectMultiplePto from "./GroupSelectMultiplePto";

// API
import { getMappedPositionsPto, getPositionsPtoTypes, TGetMappedPositionsPto } from "@/Settings/api/PtoModality/queries";
import { requestLastXQsIngestion } from "@/Settings/api/PtoModality/mutations";

// Helpers
import { getDepartmentalPosition } from "@Common/helpers/Positions";
// Images
import empty from "@/assets/images/commons/empty.png";
// Custom Styles
import "@/assets/styles/settings/PositionMapping.css";

// Types
import { RootState } from "@/redux/store.ts";

const mockedArray = [{ _id: 1 }, { _id: 2 }, { _id: 3 }];

export default function PtoModalityLayer() {
	const queryClient = useQueryClient();
	const location = useSelector((state: RootState) => state.location.location);
	const locationId = location?.locationId;

	const [lastXQsPositions, setLastXQsPositions] = useState<string[]>([]);

	/********************************************* QUERIES ************************************************/
	const [positionsPtoTypes, mappedPositionsPto] = useQueries({
		queries: [
			{
				queryKey: ["PositionsPtoTypes", locationId],
				queryFn: async () => {
					const res = await getPositionsPtoTypes(locationId as string);
					return res
						?.map((item) => {
							const payTypesOptions =
								item?.payType?.map((payType) => ({
									label: payType.trim(),
								})) || [];
							const uniquePayTypes = _.uniqBy(payTypesOptions, "label");
							return {
								position: item.position,
								options: uniquePayTypes,
							};
						})
						?.sort((a, b) => a.position.localeCompare(b.position));
				},
				enabled: Boolean(locationId),
			},
			{
				queryKey: ["MappedPositionsPto", locationId],
				queryFn: async () => {
					const res = await getMappedPositionsPto(locationId as string);
					const mappedResult = res?.mappedPositions?.map((item) => {
						return {
							...item,
							drawModality: res.drawModalities?.find(
								(draw) => getDepartmentalPosition(draw.position) === getDepartmentalPosition(item.systemPosition)
							)?.drawModality,
							ptoType:
								item?.ptoType?.map((type) => {
									const mappedType = {
										Unpaid: {
											_id: type._id,
											value: type.value,
											modality: type.modality,
										},
										"Pay Rate": {
											_id: type._id,
											value: type.value,
											modality: type.modality,
										},
										"Draw Equivalent": {
											_id: type._id,
											value: type.value,
											modality: type.modality,
										},
										"Working days proration": {
											_id: type._id,
											value: type.value,
											modality: type.modality,
											modalityDefinition: {
												excludeHolidays: type?.modalityDefinition?.excludeHolidays,
											},
										},
										Flat: {
											_id: type._id,
											value: type.value,
											modality: type.modality,
											modalityDefinition: {
												frequency: type?.modalityDefinition?.frequency,
												input: type?.modalityDefinition?.input,
												value: type?.modalityDefinition?.value,
											},
										},
										"Last xQs": {
											_id: type._id,
											value: type.value,
											modality: type.modality,
											modalityDefinition: {
												value: type?.modalityDefinition?.value,
												excludeHolidays: type?.modalityDefinition?.excludeHolidays || false,
											},
										},
									};
									return mappedType?.[type?.modality as keyof typeof mappedType];
								}) || [],
						};
					});

					const updatedLastXQsPositions: string[] = [];

					mappedResult?.forEach((pos) => {
						if (pos.ptoType.find((type) => type.modality === "Last xQs")) {
							updatedLastXQsPositions.push(pos.systemPosition);
						}
					});

					setLastXQsPositions(updatedLastXQsPositions);
					return mappedResult;
				},
				enabled: Boolean(locationId),
			},
		],
	});

	// Determine if any query is loading
	const isLoading = [positionsPtoTypes, mappedPositionsPto].some((query) => query.isLoading);

	/********************************************* MUTATIONS ************************************************/
	const { mutate: ingestLastXQs } = useMutation({ mutationFn: (input: string[]) => requestLastXQsIngestion(locationId, input) });

	/********************************************* HELPERS ************************************************/
	const filteredSelectedData = (key: string) => {
		const filtered = mappedPositionsPto?.data?.find((item) => item.systemPosition === key)?.ptoType;
		if (filtered?.length) {
			return filtered;
		} else {
			return [{}];
		}
	};

	const arePositionListsEqual = (lastXQsPositionsArr: string[], updatedLastXQsPositions: string[] | undefined) => {
		if (!lastXQsPositionsArr?.length || !updatedLastXQsPositions?.length) {
			return false;
		}

		return (
			lastXQsPositionsArr.every((item) => updatedLastXQsPositions.includes(item)) &&
			updatedLastXQsPositions.every((item) => lastXQsPositionsArr.includes(item))
		);
	};

	type MappedPositionsPto = TGetMappedPositionsPto["mappedPositions"];
	const lastXQsIngestionHandler = () => {
		const updatedModalityData = queryClient.getQueryData<MappedPositionsPto>(["MappedPositionsPto", locationId]);

		const updatedLastXQsPositions = updatedModalityData
			?.filter((pos) => pos.ptoType.some((type) => type.modality === "Last xQs"))
			?.map((pos) => pos.systemPosition);

		if (!arePositionListsEqual(lastXQsPositions, updatedLastXQsPositions) && updatedLastXQsPositions?.length) {
			ingestLastXQs(updatedLastXQsPositions, {
				onSuccess: () => {
					setLastXQsPositions(updatedLastXQsPositions);
				},
			});
		}
	};

	return (
		<BodyLayer>
			<RoundedBox className="min-h-[630px]">
				<Grid container direction="column">
					{isLoading ? (
						<Grid container item spacing={1.5}>
							<Grid item container direction="row" alignContent={"center"} alignItems={"center"}>
								<Grid item xs={2}>
									<Typography>
										<Skeleton height={25} width={110} variant="rectangular" className="rounded-[4px]" />
									</Typography>
								</Grid>
								<Grid item>
									<Typography>
										<Skeleton height={25} width={130} variant="rectangular" className="rounded-[4px]" />
									</Typography>
								</Grid>
							</Grid>
							{mockedArray.map((item) => (
								<Grid key={item._id} container item direction="row" alignContent={"center"} alignItems={"center"}>
									<Grid item xs={2}>
										<Skeleton width={150} height={21.75} />
									</Grid>

									<Grid item xs={true}>
										<Skeleton height={30} width={250} variant="rectangular" className="rounded-[4px]" />
									</Grid>
								</Grid>
							))}
						</Grid>
					) : (
						Boolean(positionsPtoTypes?.data?.length) && (
							<Grid container item spacing={1.5}>
								<Grid item container direction="row" alignContent={"center"} alignItems={"center"}>
									<Grid item xs={2}>
										<Typography className="font-medium !text-[18px] text-gray-text !font-montserrat">
											Position
										</Typography>
									</Grid>
									<Grid item>
										<Typography className="font-medium !text-[18px] text-gray-text !font-montserrat">
											PTO Type
										</Typography>
									</Grid>
								</Grid>
								{positionsPtoTypes?.data?.map((item) => {
									return (
										<Grid
											key={item.position}
											container
											item
											direction="row"
											alignContent={"center"}
											alignItems={"center"}
										>
											<Grid item xs={2}>
												<Typography className="!font-normal !text-[14.5px] text-gray-text !font-sans">
													{item.position}
												</Typography>
											</Grid>

											<Grid item xs={true}>
												<GroupSelectMultiplePto
													lastXQsIngestionHandler={lastXQsIngestionHandler}
													selectedValue={filteredSelectedData(item.position) || [{}]}
													drawModality={
														mappedPositionsPto?.data?.find(
															(user) =>
																getDepartmentalPosition(user.systemPosition) ===
																getDepartmentalPosition(item.position)
														)?.drawModality
													}
													rowTitle={item.position}
													options={item.options}
													loading={isLoading}
												/>
											</Grid>
										</Grid>
									);
								})}
							</Grid>
						)
					)}

					{!positionsPtoTypes?.data?.length && !isLoading && (
						<EmptyBody
							image={empty}
							title={"No active PTO policy"}
							bodyHeight={"510px"}
							sx={{ boxShadow: "none !important" }}
						/>
					)}
				</Grid>
			</RoundedBox>
		</BodyLayer>
	);
}
